import React, { Component, ReactNode } from 'react';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { captureEnhancedErrorContext } from '@lib/utils/enhancedErrorContext';
import { ErrorFallbackUI } from '@components/ErrorBoundary/ErrorFallbackUI';
import * as Sentry from '@sentry/react';
import AuthModule from '@modules/AuthModule';
import FullStoryModule from '@modules/FullStoryModule';
import environment from '@lib/environment';

export type ModuleErrorBoundarySize = 'full' | 'compact';

export interface ModuleErrorBoundaryProps {
  children: ReactNode;
  moduleName: string;
  fallback?: ReactNode | ((props: { reset: () => void }) => ReactNode);
  maxResets?: number; // Maximum number of resets allowed within resetWindow (default: 3)
  resetWindow?: number; // Time window in ms to track resets (default: 60000ms = 1 minute)
  /**
   * Size variant for the error fallback UI
   * - 'full': Standard fallback with more spacing, suitable for larger module areas (default)
   * - 'compact': Smaller fallback with reduced spacing, suitable for smaller modules
   */
  size?: ModuleErrorBoundarySize;
}

interface ModuleErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  resetTimestamps: number[]; // Track timestamps of reset attempts
  maxResetsExceeded: boolean; // True when max resets exceeded
}

/**
 * ModuleErrorBoundary - Scoped error boundary for individual modules
 *
 * Features:
 * - Isolates errors to specific modules (ToastsGroup, SoftPhoneLoader, etc.)
 * - Shows inline error message without disrupting rest of the app
 * - Reports errors as background errors to DiagnosticsModule
 * - Provides retry functionality with max retry limits
 * - Uses Anvil v2 components for consistent UI
 *
 * Usage:
 * Wrap individual modules or features that can fail independently:
 *
 * @example
 * <ModuleErrorBoundary moduleName="ToastModule">
 *   <ToastsGroup />
 * </ModuleErrorBoundary>
 */
export class ModuleErrorBoundary extends Component<
  ModuleErrorBoundaryProps,
  ModuleErrorBoundaryState
> {
  constructor(props: ModuleErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      resetTimestamps: [],
      maxResetsExceeded: false,
    };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ModuleErrorBoundaryState> {
    // Check if error boundaries should be enabled in development
    const enableInDev = import.meta.env.VITE_ENABLE_ERROR_BOUNDARIES === 'true';

    // In local development, don't catch UNLESS explicitly enabled for testing
    if (environment.MODE === 'development' && !enableInDev) {
      return {}; // Return empty state, don't set hasError - show React overlay
    }

    // Update state so the next render will show the fallback UI
    // Note: We only update hasError and error, preserving resetTimestamps and maxResetsExceeded
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { moduleName } = this.props;

    try {
      // Always log for debugging (helpful in all environments)
      console.error(
        `[ModuleErrorBoundary:${moduleName}] caught error:`,
        error,
        errorInfo,
      );

      // Capture enhanced error context (url, pathname, feature flags, etc.)
      const enhancedContext = captureEnhancedErrorContext();

      // Enhance Sentry context with additional information for error boundaries
      const user = AuthModule.user;
      if (user) {
        Sentry.setTags({
          'org.id': user.org.id,
          'org.name': user.org.name,
          accountId: user.accountId,
        });

        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.name,
        });
      }

      // Add FullStory session URL as context
      if (FullStoryModule.sessionUrl) {
        Sentry.setContext('fullstory', {
          sessionUrl: FullStoryModule.sessionUrl,
        });
      }

      // Add feature flags as context
      Sentry.setContext('featureFlags', enhancedContext.featureFlags);

      // Report error to DiagnosticsModule as background error
      // Module failures are less critical than global failures
      DiagnosticsModule.reportBackgroundError(
        error,
        `Module Error (${moduleName}): ${error.message}`,
        {
          priority: 'normal',
          source: `module-error-boundary-${moduleName.toLowerCase()}`,
          extra: {
            moduleName,
            componentStack: errorInfo.componentStack,
            errorInfo,
            // Enhanced context for better debugging
            url: enhancedContext.url,
            pathname: enhancedContext.pathname,
            timestamp: enhancedContext.timestamp,
            userAgent: enhancedContext.userAgent,
            featureFlags: enhancedContext.featureFlags,
          },
        },
      );

      // Note: Environment-based error boundary behavior is controlled in getDerivedStateFromError,
      // not here. componentDidCatch should only perform side effects (logging, reporting).
      // Re-throwing here would corrupt React's internal state.
    } catch (handlerError) {
      // Critical: componentDidCatch must NEVER throw, or it corrupts React's internal state
      // If error reporting fails, just log it and continue
      console.error(
        `[ModuleErrorBoundary:${moduleName}] Error handler failed:`,
        handlerError,
      );
      console.error('Original error:', error);
    }
  }

  reset = (): void => {
    const { maxResets = 3, resetWindow = 60000 } = this.props;
    const now = Date.now();

    // Filter out timestamps older than resetWindow
    // This prevents the array from growing indefinitely
    const recentTimestamps = this.state.resetTimestamps.filter(
      (timestamp) => now - timestamp < resetWindow,
    );

    // Safety limit: Cap at 10 timestamps to prevent memory issues in edge cases
    const MAX_TIMESTAMPS = 10;
    const limitedTimestamps =
      recentTimestamps.length > MAX_TIMESTAMPS
        ? recentTimestamps.slice(-MAX_TIMESTAMPS)
        : recentTimestamps;

    // Check if we've exceeded maxResets within the resetWindow
    if (limitedTimestamps.length >= maxResets) {
      // Max resets exceeded - show permanent error
      this.setState({
        maxResetsExceeded: true,
      });
      return;
    }

    // Reset is allowed - add timestamp and reset error state
    this.setState({
      hasError: false,
      error: null,
      resetTimestamps: [...limitedTimestamps, now],
      maxResetsExceeded: false,
    });
  };

  private renderCustomFallback(
    fallback: ReactNode | ((props: { reset: () => void }) => ReactNode),
  ): ReactNode {
    if (typeof fallback === 'function') {
      return fallback({ reset: this.reset });
    }
    return fallback;
  }

  private renderPermanentError(
    moduleName: string,
    size: ModuleErrorBoundarySize,
  ): ReactNode {
    const message =
      size === 'full'
        ? 'This module has crashed multiple times and cannot be recovered. Please refresh the page.'
        : 'crashed. Please refresh.';
    const testId =
      size === 'compact'
        ? 'module-error-permanent-fallback-compact'
        : 'module-error-permanent-fallback';

    return (
      <ErrorFallbackUI
        title={`${moduleName} Error`}
        message={message}
        severity='warning'
        layout='inline'
        size={size}
        permanent={true}
        testId={testId}
      />
    );
  }

  private renderDefaultError(
    moduleName: string,
    size: ModuleErrorBoundarySize,
  ): ReactNode {
    const message =
      size === 'full'
        ? "This module encountered an error and couldn't load."
        : 'error';
    const testId =
      size === 'compact'
        ? 'module-error-fallback-compact'
        : 'module-error-fallback';

    return (
      <ErrorFallbackUI
        title={`${moduleName} Error`}
        message={message}
        severity='warning'
        layout='inline'
        size={size}
        onRetry={this.reset}
        testId={testId}
      />
    );
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { fallback, size = 'full', moduleName } = this.props;
    const { maxResetsExceeded } = this.state;

    if (fallback) {
      return this.renderCustomFallback(fallback);
    }

    if (maxResetsExceeded) {
      return this.renderPermanentError(moduleName, size);
    }

    return this.renderDefaultError(moduleName, size);
  }
}
